import React, { useState, useEffect, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { api } from "../lib/apiClient";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Loader2, DollarSign, RefreshCw, Globe } from "lucide-react";
import { toast } from "sonner";

const CURRENCIES = [
  { code: "USD", label: "USD ($) — US Dollar", region: "United States" },
  { code: "CAD", label: "CAD ($) — Canadian Dollar", region: "Canada" },
  { code: "GBP", label: "GBP (£) — British Pound", region: "United Kingdom" },
  { code: "EUR", label: "EUR (€) — Euro", region: "Europe" },
  { code: "AUD", label: "AUD ($) — Australian Dollar", region: "Australia" },
];

export default function RoomCostDialog({ open, onOpenChange, redesignId, selectedIndex = 0 }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [region, setRegion] = useState("United States");
  const [currency, setCurrency] = useState("USD");

  const estimate = useCallback(async (opts = {}) => {
    if (!redesignId) return;
    const rg = opts.region ?? region;
    const cur = opts.currency ?? currency;
    setLoading(true);
    try {
      const { data } = await api.post("/ai/room-cost", { redesign_id: redesignId, selected_index: selectedIndex, region: rg, currency: cur });
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not estimate cost");
    } finally { setLoading(false); }
  }, [redesignId, selectedIndex, region, currency]);

  useEffect(() => { if (open) estimate(); /* eslint-disable-next-line */ }, [open, redesignId, selectedIndex]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0D1117] border-[#21262D] text-[#F0F6FC] max-w-xl max-h-[92vh] overflow-y-auto" data-testid="room-cost-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading text-xl">
            <DollarSign className="w-5 h-5 text-[#34D399]" /> Materials & Cost to Build
          </DialogTitle>
          <p className="text-[#8B949E] text-sm">An AI estimate of the materials in this design and what it would cost to build the room.</p>
        </DialogHeader>

        <div className="flex items-center gap-2">
          <Select value={currency} onValueChange={(v) => { const c = CURRENCIES.find((x) => x.code === v); setCurrency(v); if (c) setRegion(c.region); estimate({ currency: v, region: c ? c.region : region }); }}>
            <SelectTrigger className="w-[130px] bg-[#161B22] border-[#30363D] shrink-0" data-testid="cost-currency-select"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#161B22] border-[#30363D] text-[#F0F6FC]">
              {CURRENCIES.map((c) => <SelectItem key={c.code} value={c.code}>{c.code}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="flex items-center gap-2 flex-1 rounded-lg border border-[#30363D] bg-[#161B22] px-3">
            <Globe className="w-4 h-4 text-[#8B949E]" />
            <Input value={region} onChange={(e) => setRegion(e.target.value)} placeholder="Region (e.g. United States)"
              onKeyDown={(e) => { if (e.key === "Enter") estimate(); }}
              className="border-0 bg-transparent px-0 focus-visible:ring-0" data-testid="cost-region-input" />
          </div>
          <Button onClick={() => estimate()} disabled={loading} variant="outline" className="border-[#30363D] shrink-0" data-testid="cost-refresh-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center gap-2 py-10 text-[#8B949E]" data-testid="cost-loading">
            <Loader2 className="w-5 h-5 animate-spin text-[#34D399]" /> Estimating materials & cost…
          </div>
        ) : data ? (
          <div className="space-y-3" data-testid="cost-result">
            <div className="rounded-lg border border-[#21262D] overflow-hidden">
              <div className="grid grid-cols-12 bg-[#161B22] px-3 py-2 text-[10px] uppercase tracking-widest text-[#6E7681] font-mono-cad">
                <span className="col-span-7">Item</span><span className="col-span-2">Qty</span><span className="col-span-3 text-right">Est. cost</span>
              </div>
              {(data.items || []).map((it, i) => (
                <div key={i} className="grid grid-cols-12 px-3 py-2 text-sm border-t border-[#21262D]" data-testid={`cost-item-${i}`}>
                  <span className="col-span-7 text-[#C9D1D9]">{it.name}</span>
                  <span className="col-span-2 text-[#8B949E]">{it.qty}</span>
                  <span className="col-span-3 text-right text-[#F0F6FC] font-semibold">{it.cost}</span>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between rounded-lg bg-[#0B0F14] border border-[#34D399]/30 px-4 py-3">
              <span className="font-heading font-semibold">Total estimate</span>
              <span className="font-heading font-bold text-lg text-[#34D399]" data-testid="cost-total">{data.total}</span>
            </div>
            {data.notes && <p className="text-xs text-[#8B949E]">{data.notes}</p>}
            <p className="text-[11px] text-[#6E7681] italic">{data.source || "AI estimate"} · figures are indicative — confirm with local suppliers and contractors.</p>
          </div>
        ) : (
          <p className="text-sm text-[#6E7681] py-6 text-center">No estimate yet.</p>
        )}
      </DialogContent>
    </Dialog>
  );
}
